/**
 * Reports: water used, pump and valve run hours, energy and its cost, day by day for a
 * period, with a CSV of everything. Worked out on the server from the meters' running totals
 * and the outputs' on/off reports (packages/core/src/reports.ts).
 */
import { useState, type ReactNode } from "react";
import { api, type CropReport, type Report } from "../api.ts";
import { BarChart } from "../chart.tsx";
import { formatNumber } from "../format.ts";
import { iso, PERIODS, periodDates, reportCsv, water, type PeriodId } from "../report-text.ts";
import { go } from "../route.ts";
import { LockCard, useCan } from "../plan.tsx";
import { lazyScreen, Problem, Section, Spinner, useLoad } from "../ui.tsx";

// The chart view is a screen of its own (and its chart code): loaded when first opened.
const ChartReport = lazyScreen(() => import("./reports-chart.tsx").then((m) => m.ChartReport));

const money = (dollars: number) => dollars.toLocaleString("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: dollars >= 1000 ? 0 : 2 });

export function Reports({ accountId, staff, seasonStartMonth }: { accountId: string | undefined; staff: boolean; seasonStartMonth?: number }) {
  const allowed = useCan("reports-csv");
  if (!allowed) {
    return (
      <Section title="Reports">
        <LockCard feature="reports-csv" />
      </Section>
    );
  }
  return <ReportsBody accountId={accountId} staff={staff} seasonStartMonth={seasonStartMonth} />;
}

function ReportsBody({ accountId, staff, seasonStartMonth }: { accountId: string | undefined; staff: boolean; seasonStartMonth?: number }) {
  const [period, setPeriod] = useState<PeriodId>("7d");
  const [custom, setCustom] = useState(() => periodDates("7d", new Date()));
  const [view, setView] = useState<"farm" | "crops" | "chart">("farm");
  const dates = period === "custom" ? custom : periodDates(period, new Date(), seasonStartMonth);
  const data = useLoad(() => api.report(dates.from, dates.to, staff ? accountId : undefined), [dates.from, dates.to, accountId, staff]);
  const r = data.data?.report;

  return (
    <>
      <Section
        title="Reports"
        right={
          r &&
          view === "farm" && (
            <button className="btn ghost" onClick={() => downloadCsv(r)}>
              Download CSV
            </button>
          )
        }
      >
        <div className="chips" role="tablist">
          <button className="chip" role="tab" aria-selected={view === "farm"} aria-pressed={view === "farm"} onClick={() => setView("farm")}>
            Whole farm
          </button>
          <button className="chip" role="tab" aria-selected={view === "crops"} aria-pressed={view === "crops"} onClick={() => setView("crops")}>
            By crop
          </button>
          <button className="chip" role="tab" aria-selected={view === "chart"} aria-pressed={view === "chart"} onClick={() => setView("chart")}>
            Chart
          </button>
        </div>
        {view !== "chart" && (<>
        <div className="chips" role="group" aria-label="Period">
          {PERIODS.map(([id, label]) => (
            <button key={id} className="chip" aria-pressed={period === id} onClick={() => setPeriod(id)}>
              {label}
            </button>
          ))}
        </div>
        {period === "custom" && (
          <div className="frow" style={{ maxWidth: 420 }}>
            <div className="field">
              <label htmlFor="rep-from">From</label>
              <input id="rep-from" type="date" value={custom.from} max={custom.to} onChange={(e) => e.target.value && setCustom({ ...custom, from: e.target.value })} />
            </div>
            <div className="field">
              <label htmlFor="rep-to">To</label>
              <input id="rep-to" type="date" value={custom.to} min={custom.from} max={iso(new Date())} onChange={(e) => e.target.value && setCustom({ ...custom, to: e.target.value })} />
            </div>
          </div>
        )}
        {!!data.error && !r && <Problem error={data.error} onRetry={data.reload} />}
        {!r && !data.error && <Spinner />}
        </>)}
      </Section>
      {view === "chart" ? <ChartReport accountId={staff ? accountId : undefined} /> : view === "crops" ? <CropsReport from={dates.from} to={dates.to} accountId={staff ? accountId : undefined} /> : r && <ReportBody r={r} loading={data.loading} />}
    </>
  );
}

function ReportBody({ r, loading }: { r: Report; loading: boolean }) {
  const waterTotal = r.water.reduce((t, l) => t + l.total, 0);
  const outputs = r.run.filter((l) => l.output);
  const inputs = r.run.filter((l) => !l.output);
  const hours = outputs.reduce((t, l) => t + l.total, 0);
  const kwh = r.energy.reduce((t, l) => t + l.total, 0);
  const cost = r.energy.reduce((t, l) => t + (l.cost ?? 0), 0);
  const sumDays = (lines: Array<{ byDay: number[] }>) => r.days.map((_, i) => lines.reduce((t, l) => t + (l.byDay[i] ?? 0), 0));
  const w = water(waterTotal);
  const range = `${new Date(`${r.from}T12:00:00`).toLocaleDateString("en-AU", { day: "numeric", month: "short" })} – ${new Date(`${r.to}T12:00:00`).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })}`;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 22, opacity: loading ? 0.6 : 1, transition: "opacity .2s" }}>
      <div className="tiles">
        <Stat label="Water used" value={r.water.length ? w.value : "–"} unit={r.water.length ? w.unit : ""} foot={r.water.length ? range : "No water meters set up"} />
        <Stat label="Pump and valve hours" value={outputs.length ? formatNumber(hours) : "–"} unit={outputs.length ? "h" : ""} foot={`${outputs.reduce((t, l) => t + l.starts, 0)} starts`} />
        <Stat label="Energy" value={r.energy.length ? formatNumber(kwh) : "–"} unit={r.energy.length ? "kWh" : ""} foot={r.energy.length ? range : "No energy meters set up"} />
        <Stat
          label="Power cost"
          value={r.tariffCents && r.energy.length ? money(cost) : "–"}
          unit=""
          foot={r.tariffCents ? `at ${formatNumber(r.tariffCents)} c/kWh` : "Set the electricity price in Admin → Farm settings"}
        />
      </div>

      {r.licence && (
        <section className="card" style={{ padding: 16, display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
            <b>Water licence this season</b>
            <span className="muted small">
              since {new Date(`${r.licence.seasonFrom}T12:00:00`).toLocaleDateString("en-AU", { day: "numeric", month: "long" })}
            </span>
          </div>
          <div className="progress" role="progressbar" aria-valuenow={Math.round((100 * r.licence.usedML) / r.licence.allocationML)} aria-valuemin={0} aria-valuemax={100}>
            <div style={{ width: `${Math.min(100, (100 * r.licence.usedML) / r.licence.allocationML)}%`, background: r.licence.usedML > r.licence.allocationML * 0.9 ? "var(--warn)" : "var(--water)" }} />
          </div>
          <div className="small">
            <b>{formatNumber(r.licence.usedML)} ML</b> of {formatNumber(r.licence.allocationML)} ML used ({Math.round((100 * r.licence.usedML) / r.licence.allocationML)} %),{" "}
            {formatNumber(Math.max(0, r.licence.allocationML - r.licence.usedML))} ML left. Counts the water meters set up in Think-Control.
          </div>
        </section>
      )}

      <Section title="Water used">
        {r.water.length ? (
          <>
            <section className="card chart-card">
              <BarChart days={r.days} values={sumDays(r.water).map((v) => v / 1000)} unit="kL" label="Water used" />
            </section>
            <Table
              head={["Meter", "Total", "Share"]}
              rows={r.water.map((l) => {
                const v = water(l.total);
                return [<Item key="i" l={l} />, `${v.value} ${v.unit}`, waterTotal ? `${Math.round((100 * l.total) / waterTotal)} %` : "–"];
              })}
            />
          </>
        ) : (
          <div className="card empty small">No water meters yet. Set a digital input up as a flow meter (litres per pulse) in the device's Set up.</div>
        )}
      </Section>

      <Section title="Run hours">
        {outputs.length ? (
          <>
            <section className="card chart-card">
              <BarChart days={r.days} values={sumDays(outputs)} unit="h" color="var(--accent)" label="Run hours" />
            </section>
            <Table
              head={["Pump or valve", "Hours", "Starts", "A day"]}
              rows={outputs.map((l) => [<Item key="i" l={l} />, `${formatNumber(l.total)} h`, String(l.starts), `${formatNumber(l.total / r.days.length)} h`])}
            />
          </>
        ) : (
          <div className="card empty small">No pumps or valves have reported in this period.</div>
        )}
        {inputs.length > 0 && (
          <details className="card" style={{ padding: "10px 16px" }}>
            <summary className="small" style={{ cursor: "pointer", fontWeight: 600 }}>
              Inputs: time on ({inputs.length})
            </summary>
            <Table head={["Input", "Hours on", "Times on"]} rows={inputs.map((l) => [<Item key="i" l={l} />, `${formatNumber(l.total)} h`, String(l.starts)])} plain />
          </details>
        )}
      </Section>

      {r.energy.length > 0 && (
        <Section title="Energy and cost">
          <section className="card chart-card">
            <BarChart days={r.days} values={sumDays(r.energy)} unit="kWh" color="var(--accent)" label="Energy" />
          </section>
          <Table
            head={["Meter", "Energy", "Cost"]}
            rows={r.energy.map((l) => [<Item key="i" l={l} />, `${formatNumber(l.total)} kWh`, l.cost !== undefined ? money(l.cost) : "–"])}
          />
        </Section>
      )}
      <p className="muted small" style={{ margin: 0 }}>
        Totals come from what the meters and outputs reported. Days when a device was offline count what it reported once it was back.
      </p>
    </div>
  );
}

function Stat({ label, value, unit, foot }: { label: string; value: string; unit: string; foot: string }) {
  return (
    <div className="tile">
      <div className="lbl">{label}</div>
      <div className="big num">
        {value}
        {unit && <small>{unit}</small>}
      </div>
      <div className="foot">{foot}</div>
    </div>
  );
}

/** What was measured, with the device it's on (a link to it). */
function Item({ l }: { l: { label: string; device: string; devEui: string } }) {
  return (
    <>
      <div style={{ fontWeight: 600 }}>{l.label}</div>
      <button className="linkish small muted" onClick={() => go({ name: "device", eui: l.devEui })}>
        {l.device}
      </button>
    </>
  );
}

function Table({ head, rows, plain }: { head: string[]; rows: Array<Array<ReactNode>>; plain?: boolean }) {
  return (
    <div className={plain ? "" : "card"} style={{ overflowX: "auto" }}>
      <table className="tbl" style={{ minWidth: 0 }}>
        <thead>
          <tr>
            {head.map((h, i) => (
              <th key={h} style={i >= 1 ? { textAlign: "right" } : undefined}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              {row.map((c, j) => (
                <td key={j} className={j >= 1 ? "num" : undefined} style={j >= 1 ? { textAlign: "right", whiteSpace: "nowrap" } : undefined}>
                  {c}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function downloadCsv(r: Report) {
  const blob = new Blob([reportCsv(r)], { type: "text/csv" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `think-control-report-${r.from}-to-${r.to}.csv`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}

// ------------------------------------------------------------------ by crop

/** Water, power, cost and hours under each crop, from the pump runs switched on (or tagged) for it. */
function CropsReport({ from, to, accountId }: { from: string; to: string; accountId: string | undefined }) {
  const data = useLoad(() => api.cropReport(from, to, accountId), [from, to, accountId]);
  if (data.error && !data.data) return <Problem error={data.error} onRetry={data.reload} />;
  if (!data.data) return <Spinner />;
  const r = data.data;
  if (!r.crops.length) {
    return (
      <div className="card empty small">
        No pump runs were booked to a crop in this period. Add crops in Admin → Crops, then pick the crop when switching a pump on, or tag a pump that's
        already running on its device page.
      </div>
    );
  }
  const maxWater = Math.max(...r.crops.map((c) => c.water), 1);
  const time = (at: number) => new Date(at).toLocaleString("en-AU", { weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 22, opacity: data.loading ? 0.6 : 1 }}>
      <Section
        title="By crop"
        right={
          <button className="btn ghost" onClick={() => downloadText(cropCsv(r), `think-control-crops-${r.from}-to-${r.to}.csv`)}>
            Download CSV
          </button>
        }
      >
        <Table
          head={["Crop", "Water", "Per ha", "Energy", "Cost", "Hours", "Runs"]}
          rows={r.crops.map((c) => {
            const w = water(c.water);
            const ha = c.areaHa && c.water ? water(c.water / c.areaHa) : undefined;
            return [
              <>
                <div style={{ fontWeight: 600 }}>{c.name}</div>
                <div className="crop-bar" aria-hidden="true">
                  <div style={{ width: `${(100 * c.water) / maxWater}%` }} />
                </div>
              </>,
              c.water ? `${w.value} ${w.unit}` : "–",
              ha ? `${ha.value} ${ha.unit}/ha` : "–",
              c.kWh ? `${formatNumber(c.kWh)} kWh` : "–",
              c.cost !== undefined && c.kWh ? money(c.cost) : "–",
              `${formatNumber(c.hours)} h`,
              String(c.runs),
            ];
          })}
        />
        {!r.tariffCents && <p className="muted small" style={{ margin: 0 }}>Set the electricity price in Admin → Farm settings to see cost per crop.</p>}
      </Section>
      <Section title="Pump runs">
        <Table
          head={["When", "Crop", "Hours", "Water", "Energy"]}
          rows={r.runs.map((x) => {
            const w = water(x.water);
            return [
              <>
                <div style={{ fontWeight: 600 }}>
                  {x.outputName} · {x.device}
                </div>
                <div className="muted small">
                  {time(x.from)} → {x.end >= Date.now() - 60_000 ? "running" : time(x.end)} · {x.how === "tagged" ? "tagged" : x.how === "program" ? "program" : "switched on"} by {x.by}
                  {x.averages.length > 0 && ` · ${x.averages.map((a) => `${a.label} ${formatNumber(a.average)}${a.unit ? ` ${a.unit}` : ""}`).join(", ")}`}
                </div>
              </>,
              x.crop,
              `${formatNumber(x.hours)} h`,
              x.water ? `${w.value} ${w.unit}` : "–",
              x.kWh ? `${formatNumber(x.kWh)} kWh` : "–",
            ];
          })}
        />
        <p className="muted small" style={{ margin: 0 }}>
          A run's water and power are what its pump's meters counted while it ran. Hours only count while the board reported the pump on.
        </p>
      </Section>
    </div>
  );
}

function cropCsv(r: CropReport): string {
  const rows: Array<Array<string | number>> = [["Crop", "Area (ha)", "Water (kL)", "Energy (kWh)", "Cost (AUD)", "Hours", "Runs"]];
  const n = (v: number) => Math.round(v * 1000) / 1000;
  for (const c of r.crops) rows.push([c.name, c.areaHa ?? "", n(c.water / 1000), n(c.kWh), c.cost !== undefined ? n(c.cost) : "", n(c.hours), c.runs]);
  rows.push([]);
  rows.push(["Run start", "Run end", "Device", "Pump", "Crop", "Hours", "Water (kL)", "Energy (kWh)", "By"]);
  for (const x of r.runs) rows.push([new Date(x.from).toISOString(), new Date(x.end).toISOString(), x.device, x.outputName, x.crop, n(x.hours), n(x.water / 1000), n(x.kWh), x.by]);
  const cell = (v: string | number) => (typeof v === "number" ? String(v) : /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
  return rows.map((row) => row.map(cell).join(",")).join("\r\n");
}

function downloadText(text: string, name: string) {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([text], { type: "text/csv" }));
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}
